import _get from 'lodash/get';

import { CURIOSO_SKIN } from '../skillScan/data/constants';
import { persistStateTypes } from '../reducers/persistStateReducer';
import GAME_IDS from '../hub/data/GameIds';
import { API_URL } from '../api/endpoints';
import { aquaAxiosWithCreds, callAsyncWithRetry } from '../../lib/utilityFunctions';
import { showStandardErrorPage } from '../components/GlobalErrorHandler/actions';
import { reportQuestStepAutoComplete } from '../hub/actions/metagameSearchlight';
import { RETRY_TIMEOUT } from '../hub/data/constants';
import { resendFailedObservations } from '../middleware/observationApiActions';
import { resendFailedWriteResults } from '../middleware/resultsApiActions';
import { reportGameStateAPIError } from './persistStateActions';
import { handleAuthFailure } from '../api/apiErrors';

const METAGAME_ID = GAME_IDS.METAGAME;

const validateAllGamesResponseHandler = (gamestates, dispatch) => {
  if (gamestates) {
    let data = { ...gamestates };
    const curiosoSkin = _get(data[GAME_IDS.METAGAME_CURIOSO_SELECTED], 'GameData.gameDetails.curioso.skin', '');
    if (curiosoSkin && !Object.values(CURIOSO_SKIN).includes(curiosoSkin)) {
      data = {
        ...data,
        [GAME_IDS.METAGAME_CURIOSO_SELECTED]: {
          ...data[GAME_IDS.METAGAME_CURIOSO_SELECTED],
          GameData: {
            ...data[GAME_IDS.METAGAME_CURIOSO_SELECTED].GameData,
            gameDetails: {
              ...data[GAME_IDS.METAGAME_CURIOSO_SELECTED].GameData.gameDetails,
              curioso: {
                ...data[GAME_IDS.METAGAME_CURIOSO_SELECTED].GameData.gameDetails.curioso,
                skin: CURIOSO_SKIN.SCALY,
              },
            },
          },
        },
      };
    }

    // check for observation API failures and resend
    const observationApiFailureData = _get(data[GAME_IDS.API_FAILURES], 'GameData.gameDetails.observationApiFailures', {});
    if (observationApiFailureData && Object.keys(observationApiFailureData).length) {
      dispatch(resendFailedObservations(observationApiFailureData));
    }

    // check for /writeResult failures and resend
    const writeResultFailureData = _get(data[GAME_IDS.API_FAILURES], 'GameData.gameDetails.writeResultFailures', {});
    if (writeResultFailureData && Object.keys(writeResultFailureData).length) {
      dispatch(resendFailedWriteResults(writeResultFailureData));
    }

    const allGameIds = { ...GAME_IDS };

    // any game that does not have a bucket yet, add blank object for data
    Object.keys(allGameIds).forEach((key) => {
      const id = allGameIds[key];
      if (!data[id]) {
        data[id] = {};
      }
    });

    return data;
  }

  return gamestates;
};

/**
 * Redux Thunk Action - Function for getting all games state
 * @return {Promise} Games state resolution
 */
export const getAllGamesState = () => async (dispatch, getState) => {
  // Request started
  dispatch({
    type: persistStateTypes.REQUEST_GAMESTATE_ALL,
    gameId: METAGAME_ID,
  });

  const asyncRetryFn = async () => {
    // First resolve the authentication data
    const {
      persistStateReducer: { studentId, placedMethod, placementPeriod, placementGrade },
      appSessionReducer: { product },
    } = getState();

    const { data } = await aquaAxiosWithCreds(`${API_URL}/gameState?userId=${studentId}`);
    const stateData = validateAllGamesResponseHandler(data, dispatch);

    dispatch({
      type: persistStateTypes.REQUESTRESPONSE_GAMESTATE_ALL,
      gameId: METAGAME_ID,
      stateData,
      product,
      placementData: { placedMethod, placementPeriod, placementGrade },
    });
    dispatch(reportQuestStepAutoComplete());
  };

  const asyncRetryConfig = {
    interval: RETRY_TIMEOUT,
    logger: async (error, { timesAttempted, isDoneRetrying }) => {
      reportGameStateAPIError(
        dispatch, error, 'API: gameState', `API: getAllGamesState | Attempt - ${timesAttempted}`,
      );
      handleAuthFailure(error);
      if (isDoneRetrying) {
        // This API is part of loading percentage screen.
        // Clear loading UX to show Error screen
        window.reactLoaded();
        // Show Uh Oh error screen
        dispatch(showStandardErrorPage(error.name, error.message));
      }
    },
  };

  return callAsyncWithRetry(asyncRetryFn, asyncRetryConfig);
};

export default {};
